import { ReactNode, useEffect, useMemo, useState } from "react";
import { Form, Nav, OverlayTrigger, Tooltip } from "react-bootstrap";
import { CheckCircleFill, ChevronDown, InfoCircleFill } from "react-bootstrap-icons";
import { HtmlEditor } from "./Controls/HtmlEditor";
import "./FacilityPlanEditPage.scss";

export type PlanTabId = "details" | "content" | "scheduling" | "payments" | "cancellation";

export function planFormTabs(isUsageBasedBilling: boolean): { id: PlanTabId; label: string }[] {
    const items: { id: PlanTabId; label: string }[] = [
        { id: "details", label: "Details" },
        { id: "content", label: "Content" },
    ];
    if (!isUsageBasedBilling) {
        items.push(
            { id: "scheduling", label: "Scheduling & price" },
            { id: "payments", label: "Payments & fees" },
            { id: "cancellation", label: "Cancellation" },
        );
    }
    return items;
}

export function usePlanSectionNav(isUsageBasedBilling: boolean) {
    const tabs = useMemo(() => planFormTabs(isUsageBasedBilling), [isUsageBasedBilling]);
    const [activeSection, setActiveSection] = useState<PlanTabId>("details");

    useEffect(() => {
        const sections = tabs
            .map((t) => document.getElementById(`plan-section-${t.id}`))
            .filter((el): el is HTMLElement => el != null);
        if (!sections.length) return;

        const root = sections[0].closest(".scroll-y-auto") as HTMLElement | null;
        const obs = new IntersectionObserver(
            (entries) => {
                const visible = entries
                    .filter((e) => e.isIntersecting)
                    .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
                const id = visible[0]?.target.id.replace("plan-section-", "") as PlanTabId | undefined;
                if (id) setActiveSection(id);
            },
            { root, rootMargin: "-20% 0px -60% 0px", threshold: 0.05 },
        );
        sections.forEach((s) => obs.observe(s));
        return () => obs.disconnect();
    }, [tabs]);

    useEffect(() => {
        if (!tabs.some((t) => t.id === activeSection)) setActiveSection("details");
    }, [tabs, activeSection]);

    const jumpTo = (id: PlanTabId) => {
        setActiveSection(id);
        document.getElementById(`plan-section-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return { tabs, activeSection, jumpTo };
}

export function PlanSectionTabs({
    tabs,
    activeSection,
    onJump,
}: {
    tabs: { id: PlanTabId; label: string }[];
    activeSection: PlanTabId;
    onJump: (id: PlanTabId) => void;
}) {
    return (
        <Nav variant="underline" activeKey={activeSection} className="plan-edit-tabs">
            {tabs.map((tab) => (
                <Nav.Item key={tab.id}>
                    <Nav.Link as="button" type="button" eventKey={tab.id} onClick={() => onJump(tab.id)}>
                        {tab.label}
                    </Nav.Link>
                </Nav.Item>
            ))}
        </Nav>
    );
}

export function PlanSectionCard({
    id,
    title,
    description,
    children,
}: {
    id: PlanTabId;
    title: string;
    description: string;
    children: ReactNode;
}) {
    return (
        <section id={`plan-section-${id}`} className="plan-edit-section card mb-3">
            <div className="card-body">
                <h5 className="fw-semibold mb-1">{title}</h5>
                <p className="text-secondary small mb-3">{description}</p>
                {children}
            </div>
        </section>
    );
}

function stripHtml(html: string | null | undefined) {
    return (html ?? "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

export function CollapsibleHtmlField({
    label,
    hint,
    value,
    onChange,
    expanded,
    onToggle,
    placeholder,
    error,
    canEdit,
}: {
    label: string;
    hint?: string;
    value: string;
    onChange: (html: string) => void;
    expanded: boolean;
    onToggle: () => void;
    placeholder: string;
    error?: string;
    canEdit: boolean;
}) {
    const added = !!stripHtml(value);
    return (
        <div className="mb-3">
            <button
                type="button"
                className={`plan-html-toggle ${error ? "is-invalid" : ""}`}
                onClick={onToggle}
                aria-expanded={expanded}
            >
                <div className="min-w-0 flex-grow-1">
                    <div className="d-flex align-items-center gap-2">
                        <CheckCircleFill
                            size={16}
                            className={`flex-shrink-0 ${added ? "text-success" : "text-secondary opacity-25"}`}
                        />
                        <span className="fw-semibold">{label}</span>
                    </div>
                    {!expanded && (
                        <div className="small text-secondary ms-4">
                            {added ? "Added" : "Not added"}
                            {" — "}
                            <span className="text-primary">
                                {added ? (canEdit ? "View / Edit" : "View") : "Add"}
                            </span>
                        </div>
                    )}
                </div>
                <ChevronDown className={`plan-html-chevron ${expanded ? "is-open" : ""}`} />
            </button>
            {expanded && (
                <div className="mt-2">
                    {hint && <div className="text-muted small mb-2">{hint}</div>}
                    <HtmlEditor
                        value={value}
                        onChange={onChange}
                        placeholder={placeholder}
                        isInvalid={!!error}
                        disabled={!canEdit}
                        showToolbar={canEdit}
                    />
                </div>
            )}
            {error && (
                <Form.Text className="d-block text-danger ms-1">{error}</Form.Text>
            )}
        </div>
    );
}

export function ProrateLabel() {
    return (
        <>
            Prorate
            <OverlayTrigger
                placement="top"
                overlay={
                    <Tooltip id="prorate-tooltip">
                        1/2 Monthly cuts the rate in half after the middle of the month. Daily reduces the rate for each remaining day.
                    </Tooltip>
                }
            >
                <span
                    className="d-inline-flex align-items-center"
                    style={{ cursor: "help", marginLeft: "4px" }}
                >
                    <InfoCircleFill className="icon-shift-up" size={14} />
                </span>
            </OverlayTrigger>
        </>
    );
}
